import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { forecastsApi, budgetsApi } from '../services/api';
import { formatDate, formatDateWithMonth } from '../lib/dateUtils';
import { generateStandardName, getBuCode, sortAlphabetically, sortFiscalYearsNewestFirst } from '../lib/namingUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  TrendingUp,
  Target,
  Calendar,
  Building2,
  ArrowLeft,
  Trash2,
  Edit3,
  Copy,
  ChevronRight,
  FileText,
  BarChart3,
  RefreshCw,
  CheckCircle2,
  Archive,
  RotateCcw
} from 'lucide-react';
import api from '../services/api';

const MONTHS = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
const MONTH_KEYS = ['jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar', 'apr', 'may', 'jun'];

const FORECAST_TYPES = [
  { value: 'base', label: 'Base Case', icon: Target, color: 'emerald' },
  { value: 'best_case', label: 'Best Case', icon: TrendingUp, color: 'blue' },
  { value: 'worst_case', label: 'Worst Case', icon: BarChart3, color: 'amber' },
  { value: 'rolling', label: 'Rolling Forecast', icon: RefreshCw, color: 'purple' }
];

const ForecastsPage = () => {
  const { user, isAdmin } = useAuth();
  const { isDark, setSelectedBu: setThemeBu } = useTheme();
  const navigate = useNavigate();
  
  const [forecasts, setForecasts] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [availableBudgets, setAvailableBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBULocal, setSelectedBULocal] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, forecast: null });
  const [rolloverDialog, setRolloverDialog] = useState({ open: false, forecast: null });
  
  // Wrapper to update theme context logo when BU changes
  const setSelectedBU = (buId) => {
    setSelectedBULocal(buId);
    if (buId && buId !== 'all') {
      const bu = businessUnits.find(b => b.id === buId);
      if (setThemeBu) setThemeBu(buId, bu?.logo_url);
    } else if (setThemeBu) {
      setThemeBu('', null);
    }
  };
  const selectedBU = selectedBULocal;
  
  // Create form state
  const [formData, setFormData] = useState({
    name: '',
    fiscal_year: 'FY 2025/2026',
    business_unit_id: '',
    forecast_type: 'base',
    source_budget_id: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Sort business units alphabetically
  const sortedBusinessUnits = useMemo(() => sortAlphabetically(businessUnits, 'name'), [businessUnits]);
  
  // Check if user has BU restriction
  const userBuId = user?.business_unit_id;
  const canSeeAllBUs = isAdmin() || user?.is_cfo || !userBuId;

  // Auto-generate forecast name when BU, FY, or type changes
  useEffect(() => {
    if (formData.business_unit_id && formData.fiscal_year && formData.forecast_type) {
      const selectedBuObj = businessUnits.find(bu => bu.id === formData.business_unit_id);
      const buCode = getBuCode(selectedBuObj);
      const typeLabel = FORECAST_TYPES.find(t => t.value === formData.forecast_type)?.label || 'Forecast';
      const cleanFY = formData.fiscal_year.replace('FY ', '');
      const generatedName = generateStandardName(buCode, cleanFY, typeLabel.replace(/\s+/g, ''));
      setFormData(prev => ({ ...prev, name: generatedName }));
    }
  }, [formData.business_unit_id, formData.fiscal_year, formData.forecast_type, businessUnits]);

  useEffect(() => {
    loadData();
  }, [selectedBU, selectedType]);

  // Set default BU filter on load
  useEffect(() => {
    if (userBuId && !canSeeAllBUs) {
      setSelectedBU(userBuId);
    }
  }, [userBuId, canSeeAllBUs]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load business units
      const busResponse = await api.get('/settings/business-units');
      setBusinessUnits(busResponse.data);
      
      // Load budgets for copying (approved, pending, and draft)
      const budgetsResponse = await budgetsApi.getAll();
      // Filter to only include approved, pending, and draft (exclude rejected)
      const copyableBudgets = budgetsResponse.data.filter(b => 
        ['approved', 'pending', 'draft'].includes(b.status)
      );
      setAvailableBudgets(copyableBudgets);
      
      // Load forecasts with filters
      const params = {};
      // Apply user BU restriction if not admin/CFO
      if (!canSeeAllBUs && userBuId) {
        params.business_unit_id = userBuId;
      } else if (selectedBU !== 'all') {
        params.business_unit_id = selectedBU;
      }
      if (selectedType !== 'all') params.forecast_type = selectedType;
      
      const forecastsResponse = await forecastsApi.getAll(params);
      setForecasts(forecastsResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load forecasts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.business_unit_id || !formData.fiscal_year) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      const bu = businessUnits.find(b => b.id === formData.business_unit_id);
      
      const response = await forecastsApi.create({
        ...formData,
        business_unit_name: bu?.name
      });
      
      toast.success(response.data.message || 'Forecast created successfully');
      setCreateDialog(false);
      setFormData({
        name: '',
        fiscal_year: 'FY 2025/2026',
        business_unit_id: '',
        forecast_type: 'base',
        source_budget_id: '',
        description: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating forecast:', error);
      toast.error('Failed to create forecast');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.forecast) return;
    
    try {
      await forecastsApi.delete(deleteDialog.forecast.id);
      toast.success('Forecast deleted');
      setDeleteDialog({ open: false, forecast: null });
      loadData();
    } catch (error) {
      console.error('Error deleting forecast:', error);
      toast.error('Failed to delete forecast');
    }
  };

  const handleStatusChange = async (forecastId, newStatus) => {
    try {
      await forecastsApi.updateStatus(forecastId, newStatus);
      toast.success(`Forecast ${newStatus === 'active' ? 'activated' : newStatus === 'archived' ? 'archived' : 'set to draft'}`);
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleRollover = async () => {
    if (!rolloverDialog.forecast) return;
    
    try {
      setSubmitting(true);
      const forecast = rolloverDialog.forecast;
      
      // Calculate next fiscal year
      const currentFY = forecast.fiscal_year; // e.g., "FY 2025/2026"
      const match = currentFY.match(/FY (\d{4})\/(\d{4})/);
      let nextFY = currentFY;
      if (match) {
        const year1 = parseInt(match[1]) + 1;
        const year2 = parseInt(match[2]) + 1;
        nextFY = `FY ${year1}/${year2}`;
      }
      
      // Get BU code for naming
      const selectedBuObj = businessUnits.find(bu => bu.id === forecast.business_unit_id);
      const buCode = getBuCode(selectedBuObj);
      const typeLabel = FORECAST_TYPES.find(t => t.value === forecast.forecast_type)?.label || 'Forecast';
      const cleanFY = nextFY.replace('FY ', '');
      const newName = generateStandardName(buCode, cleanFY, typeLabel.replace(/\s+/g, ''));
      
      // Create new forecast with copied line items via API
      await forecastsApi.rollover(forecast.id, nextFY, newName);
      
      toast.success(`Forecast rolled over to ${nextFY}`);
      setRolloverDialog({ open: false, forecast: null });
      loadData();
    } catch (error) {
      console.error('Error rolling over forecast:', error);
      toast.error('Failed to rollover forecast');
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeInfo = (type) => {
    return FORECAST_TYPES.find(t => t.value === type) || FORECAST_TYPES[0];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300">Draft</Badge>;
      case 'active':
        return <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300">Active</Badge>;
      case 'archived':
        return <Badge variant="outline" className="bg-zinc-100 text-zinc-700 border-zinc-300">Archived</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Group forecasts by type
  const groupedForecasts = useMemo(() => {
    const groups = {};
    FORECAST_TYPES.forEach(type => {
      groups[type.value] = forecasts.filter(f => f.forecast_type === type.value);
    });
    return groups;
  }, [forecasts]);

  return (
    <div className={`p-6 ${isDark ? 'bg-zinc-950 text-white' : 'bg-slate-50 text-slate-900'}`} data-testid="forecasts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className={isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100'}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Forecasts</h1>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
              Create and manage financial forecasts
            </p>
          </div>
        </div>
        
        <Button 
          onClick={() => setCreateDialog(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="create-forecast-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Forecast
        </Button>
      </div>

      {/* Forecast Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {FORECAST_TYPES.map((type) => {
          const Icon = type.icon;
          const count = groupedForecasts[type.value]?.length || 0;
          const isSelected = selectedType === type.value;
          
          return (
            <div
              key={type.value}
              onClick={() => setSelectedType(isSelected ? 'all' : type.value)}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                isSelected
                  ? `border-${type.color}-500 bg-${type.color}-500/10`
                  : isDark 
                    ? 'bg-zinc-900 border-zinc-800 hover:border-zinc-700' 
                    : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`type-card-${type.value}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-6 h-6 text-${type.color}-500`} />
                <span className="text-2xl font-bold">{count}</span>
              </div>
              <p className="font-medium">{type.label}</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className={`p-4 rounded-lg border mb-6 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-wrap items-center gap-4">
          <Select 
            value={selectedBU} 
            onValueChange={setSelectedBU}
            disabled={!canSeeAllBUs}
          >
            <SelectTrigger className={`w-[200px] ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="bu-filter">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Business Unit" />
            </SelectTrigger>
            <SelectContent>
              {canSeeAllBUs && <SelectItem value="all">All Business Units</SelectItem>}
              {sortedBusinessUnits.map((bu) => (
                <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className={`w-[200px] ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="type-filter">
              <Target className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Forecast Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {FORECAST_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {(selectedBU !== 'all' || selectedType !== 'all') && canSeeAllBUs && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedBU('all'); setSelectedType('all'); }}
              className={isDark ? 'text-zinc-400' : 'text-slate-500'}
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>

      {/* Forecasts List */}
      <div className={`rounded-lg border ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-slate-200'}`}>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>Loading forecasts...</p>
          </div>
        ) : forecasts.length === 0 ? (
          <div className="p-8 text-center">
            <Target className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
            <p className={isDark ? 'text-zinc-400' : 'text-slate-500'}>No forecasts found</p>
            <p className={`text-sm mt-2 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
              Create a forecast to start planning
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {forecasts.map((forecast) => {
              const typeInfo = getTypeInfo(forecast.forecast_type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <div 
                  key={forecast.id}
                  className={`p-4 hover:${isDark ? 'bg-zinc-800/50' : 'bg-slate-50'} transition-colors`}
                  data-testid={`forecast-${forecast.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg bg-${typeInfo.color}-500/10`}>
                        <TypeIcon className={`w-5 h-5 text-${typeInfo.color}-500`} />
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{forecast.name}</h3>
                          {getStatusBadge(forecast.status)}
                          <Badge variant="outline" className={isDark ? 'border-zinc-700' : ''}>
                            {typeInfo.label}
                          </Badge>
                        </div>
                        
                        <div className={`flex items-center gap-4 text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                          <span><Building2 className="w-3 h-3 inline mr-1" />{forecast.business_unit_name}</span>
                          <span><Calendar className="w-3 h-3 inline mr-1" />{forecast.fiscal_year}</span>
                          <span>Created {formatDateWithMonth(forecast.created_at)}</span>
                        </div>
                        
                        {forecast.description && (
                          <p className={`text-sm mt-1 ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                            {forecast.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {forecast.status === 'draft' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(forecast.id, 'active')}
                          className={isDark ? 'border-zinc-700 hover:bg-zinc-800' : ''}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Activate
                        </Button>
                      )}
                      
                      {forecast.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusChange(forecast.id, 'archived')}
                          className={isDark ? 'border-zinc-700 hover:bg-zinc-800' : ''}
                        >
                          <Archive className="w-4 h-4 mr-1" />
                          Archive
                        </Button>
                      )}
                      
                      {/* Rollover to Next FY Button */}
                      {(forecast.status === 'active' || forecast.status === 'archived') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRolloverDialog({ open: true, forecast })}
                          className={`text-violet-600 border-violet-500/50 hover:bg-violet-500/10 ${isDark ? '' : ''}`}
                          data-testid={`rollover-forecast-${forecast.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Rollover
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/forecasts/${forecast.id}`)}
                        className={isDark ? 'border-zinc-700 hover:bg-zinc-800' : ''}
                        data-testid={`view-forecast-${forecast.id}`}
                      >
                        <Edit3 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, forecast })}
                        className="text-red-500 border-red-500/50 hover:bg-red-500/10"
                        data-testid={`delete-forecast-${forecast.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Forecast Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
          <DialogHeader>
            <DialogTitle>Create Forecast</DialogTitle>
            <DialogDescription>
              Create a new financial forecast. You can copy from an existing budget or start from scratch.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                Forecast Name *
              </label>
              <Input
                placeholder="e.g., Q1 2026 Rolling Forecast"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                data-testid="forecast-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                  Business Unit *
                </label>
                <Select 
                  value={formData.business_unit_id} 
                  onValueChange={(value) => setFormData({ ...formData, business_unit_id: value })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="forecast-bu-select">
                    <SelectValue placeholder="Select BU" />
                  </SelectTrigger>
                  <SelectContent>
                    {businessUnits.map((bu) => (
                      <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                  Fiscal Year *
                </label>
                <Select 
                  value={formData.fiscal_year} 
                  onValueChange={(value) => setFormData({ ...formData, fiscal_year: value })}
                >
                  <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="forecast-fy-select">
                    <SelectValue placeholder="Select FY" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FY 2024/2025">FY 2024/2025</SelectItem>
                    <SelectItem value="FY 2025/2026">FY 2025/2026</SelectItem>
                    <SelectItem value="FY 2026/2027">FY 2026/2027</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                Forecast Type
              </label>
              <Select 
                value={formData.forecast_type} 
                onValueChange={(value) => setFormData({ ...formData, forecast_type: value })}
              >
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="forecast-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {FORECAST_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                Copy from Budget (Optional)
              </label>
              <Select 
                value={formData.source_budget_id || "none"} 
                onValueChange={(value) => setFormData({ ...formData, source_budget_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`} data-testid="forecast-source-select">
                  <SelectValue placeholder="Start from scratch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Start from scratch</SelectItem>
                  {availableBudgets.map((budget) => (
                    <SelectItem key={budget.id} value={budget.id}>
                      {budget.name} ({budget.business_unit_name}) - {budget.status === 'approved' ? '✓ Approved' : budget.status === 'pending' ? '⏳ Pending' : '📝 Draft'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className={`text-xs mt-1 ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                Copy figures from an existing budget as a starting point
              </p>
            </div>
            
            <div>
              <label className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                Description (Optional)
              </label>
              <Textarea
                placeholder="Add notes about this forecast..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`mt-1 ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
                rows={3}
                data-testid="forecast-description-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialog(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting || !formData.name || !formData.business_unit_id}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="create-forecast-submit"
            >
              {submitting ? 'Creating...' : 'Create Forecast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, forecast: deleteDialog.forecast })}>
        <DialogContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
          <DialogHeader>
            <DialogTitle>Delete Forecast</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteDialog.forecast?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, forecast: null })}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-forecast"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rollover Confirmation Dialog */}
      <Dialog open={rolloverDialog.open} onOpenChange={(open) => setRolloverDialog({ open, forecast: rolloverDialog.forecast })}>
        <DialogContent className={isDark ? 'bg-zinc-900 border-zinc-800' : ''}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-violet-600" />
              Rollover Forecast to Next Financial Year
            </DialogTitle>
            <DialogDescription>
              This will create a copy of "{rolloverDialog.forecast?.name}" for the next financial year with all line items preserved.
            </DialogDescription>
          </DialogHeader>
          
          {rolloverDialog.forecast && (
            <div className={`p-4 rounded-sm ${isDark ? 'bg-zinc-800' : 'bg-slate-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
                    Current: {rolloverDialog.forecast.fiscal_year}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                    {rolloverDialog.forecast.business_unit_name}
                  </p>
                </div>
                <ChevronRight className={`w-5 h-5 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`} />
                <div>
                  <p className={`text-sm font-medium text-violet-600`}>
                    Next: {(() => {
                      const match = rolloverDialog.forecast.fiscal_year?.match(/FY (\d{4})\/(\d{4})/);
                      if (match) {
                        return `FY ${parseInt(match[1]) + 1}/${parseInt(match[2]) + 1}`;
                      }
                      return 'Next FY';
                    })()}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-500'}`}>
                    New draft forecast
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRolloverDialog({ open: false, forecast: null })}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRollover}
              disabled={submitting}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="confirm-rollover-forecast"
            >
              {submitting ? 'Rolling over...' : 'Rollover Forecast'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForecastsPage;
